import { type components } from "@njbsoft/types";

type PermissionRegistry = components["schemas"]["PermissionRegistry"];
export type DomainGroup = PermissionRegistry["domains"][number];
export type EntityDescriptor = DomainGroup["entities"][number];

export const CRUD_OPS = ["create", "read", "update", "delete"] as const;
export type CrudOp = (typeof CRUD_OPS)[number];

export interface GrantedToken {
  entity: string;
  operation: string;
}

export function tokenKey(entity: string, operation: string): string {
  return `${entity}:${operation}`;
}

/** Builds a fast-lookup Set of granted "entity:operation" tokens. */
export function grantedTokenSet(perms: readonly GrantedToken[] | undefined): Set<string> {
  const set = new Set<string>();
  for (const p of perms ?? []) set.add(tokenKey(p.entity, p.operation));
  return set;
}

export interface CrudCell {
  op: CrudOp;
  legal: boolean;
}

/** The 4 fixed CRUD columns for an entity, each flagged legal (checkbox) or illegal (—). */
export function crudColumns(entity: EntityDescriptor): CrudCell[] {
  const legal = new Set(entity.operations);
  return CRUD_OPS.map((op) => ({ op, legal: legal.has(op) }));
}

/** The non-CRUD action verbs for an entity (e.g. transfer, review), in registry order. */
export function actionVerbs(entity: EntityDescriptor): string[] {
  const crud = new Set<string>(CRUD_OPS);
  return entity.operations.filter((op) => !crud.has(op));
}

export interface DomainStats {
  granted: number;
  total: number;
  entities: number;
}

/** Per-domain "X of Y granted · N entities" where Y is the sum of legal ops. */
export function domainStats(domain: DomainGroup, granted: ReadonlySet<string>): DomainStats {
  let total = 0;
  let g = 0;
  for (const e of domain.entities) {
    total += e.operations.length;
    for (const op of e.operations) if (granted.has(tokenKey(e.entity, op))) g += 1;
  }
  return { granted: g, total, entities: domain.entities.length };
}

/**
 * A domain is read-only when every entity in it exposes only the `read`
 * operation (e.g. the Audit domain's `audit_log`). Drives the "(read-only)"
 * header annotation per the permissions-matrix mockup.
 */
export function domainIsReadOnly(domain: DomainGroup): boolean {
  return domain.entities.every((e) => e.operations.every((op) => op === "read"));
}

/** True when `op` was introduced on this entity after the org's last-seen watermark. */
export function operationIsNew(entity: EntityDescriptor, op: string): boolean {
  return entity.new_operations.includes(op);
}

/** True when a domain contains any newly-introduced entity OR operation — drives the domain-head "new" dot. */
export function domainHasNew(domain: DomainGroup): boolean {
  return domain.entities.some((e) => e.is_new || e.new_operations.length > 0);
}

/**
 * Count of newly-introduced capabilities across the registry = (new entities) +
 * (new operations). Drives the update-awareness banner's "N new" count.
 */
export function countNewCapabilities(domains: readonly DomainGroup[]): number {
  let n = 0;
  for (const d of domains) {
    for (const e of d.entities) {
      if (e.is_new) n += 1;
      n += e.new_operations.length;
    }
  }
  return n;
}

/** Every legal token of every entity: what the locked Administrators group holds. */
export function allLegalTokens(entities: readonly EntityDescriptor[]): Set<string> {
  const set = new Set<string>();
  for (const e of entities) for (const op of e.operations) set.add(tokenKey(e.entity, op));
  return set;
}

/**
 * Operations whose grant includes the entity's `read`, mirroring the server's
 * `operationsIncludingRead`: a group holding one of them holds read whether or
 * not a read row is stored, so the matrix shows read ticked and locked beside
 * it.
 */
export const OPERATIONS_INCLUDING_READ: Readonly<Record<string, readonly string[]>> = {
  bulk_edits: ["run", "revert"],
};

/**
 * The operations of `entity` that include its read, in table order (empty for
 * an entity with none). Own-property lookup: an entity token that collides
 * with an Object.prototype member reads as "none", not as an inherited value.
 */
export function operationsIncludingReadOf(entity: string): readonly string[] {
  return Object.hasOwn(OPERATIONS_INCLUDING_READ, entity)
    ? (OPERATIONS_INCLUDING_READ[entity] ?? [])
    : [];
}

/** The operations of `entity` held in `granted` that include its read, in table order. */
export function readIncludedBy(entity: string, granted: ReadonlySet<string>): string[] {
  return operationsIncludingReadOf(entity).filter((op) => granted.has(tokenKey(entity, op)));
}

/**
 * The tokens the matrix shows held: `granted` plus the read each held
 * operation in `OPERATIONS_INCLUDING_READ` includes. `granted` itself comes
 * back when nothing is added, so a memoized consumer keeps its reference.
 */
export function withIncludedReads(granted: ReadonlySet<string>): ReadonlySet<string> {
  let shown: Set<string> | null = null;
  for (const entity of Object.keys(OPERATIONS_INCLUDING_READ)) {
    const read = tokenKey(entity, "read");
    if (granted.has(read) || readIncludedBy(entity, granted).length === 0) continue;
    shown ??= new Set(granted);
    shown.add(read);
  }
  return shown ?? granted;
}

/** Filter predicate: entity label or snake_case token contains the query (case-insensitive). */
export function entityMatchesQuery(entity: EntityDescriptor, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return entity.label.toLowerCase().includes(q) || entity.entity.toLowerCase().includes(q);
}
